import { useEffect, useRef, useState } from 'react';
import type { NoteView, ObjectiveSummaryView } from '@contracts';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import { Plus, ArrowUpRight, Trash2 } from '@ui/shared/icons';
import { PickerOptionRow } from '@ui/desktop/objective-form/FormPrimitives';
import { CollapsibleCard } from './CollapsibleCard';

const PRIORITY_CAP = 5;

const INPUT_STYLE = {
  width: '100%',
  minHeight: 'var(--touch-target-min)',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-subtle)',
  background: 'var(--surface-sunken)',
  color: 'var(--text-primary)',
  fontSize: 'var(--type-body-size)',
  fontFamily: 'var(--font-family)',
} as const;

/**
 * Phone Brain dump (R8.11, today-phone-parity.md §4.6). Capture-only,
 * identical interaction to desktop — no checkbox, no date, no objective
 * chip on a row. Promote/Delete are ALWAYS visible (never hover-reveal).
 */
export function BrainDumpCardPhone({
  notes,
  objectives,
  priorityCount,
  onChanged,
  isLast,
}: {
  readonly notes: readonly NoteView[];
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly priorityCount: number;
  readonly onChanged: () => void;
  readonly isLast: boolean;
}) {
  const api = useDomainApi();
  const [draft, setDraft] = useState('');

  function addNote() {
    const text = draft.trim();
    if (text.length === 0) return;
    void api.createNote(text).then(() => {
      setDraft('');
      onChanged();
    });
  }

  // ux-designer audit flagged this summary as missing the "Brain dump · "
  // prefix from today-phone-parity.md §4.1's copy table. Deliberately NOT
  // added: CollapsibleCard renders `title` and `summary` as two separate
  // always-visible text spans with no aria-label, so their text
  // concatenates into one accessible name -- prefixing the summary would
  // make both the visible text AND the announced name repeat "Brain dump"
  // twice back-to-back. Spec updated to match (today-phone-parity.md §4.1
  // changelog) rather than introducing that duplication.
  const summary = notes.length > 0 ? `${notes.length} note${notes.length === 1 ? '' : 's'}` : undefined;

  return (
    <CollapsibleCard title="Brain dump" summary={summary} isLast={isLast}>
      <div>
        {notes.length === 0 && (
          <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Nothing dumped yet — type anything, sort it out later.
          </p>
        )}
        {notes.map((note) => (
          <BrainDumpRowPhone
            key={note.noteId}
            note={note}
            objectives={objectives}
            priorityCapReached={priorityCount >= PRIORITY_CAP}
            onChanged={onChanged}
          />
        ))}
      </div>

      <div
        style={{
          marginTop: 'var(--space-2)',
          paddingTop: 'var(--space-2)',
          borderTop: notes.length > 0 ? '1px solid var(--border-subtle)' : 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}
      >
        <Plus size={16} color="var(--text-muted)" aria-hidden="true" />
        <input
          aria-label="Quick-add a brain-dump note"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') addNote();
          }}
          placeholder="Type anything..."
          style={{ ...INPUT_STYLE, border: 'none', background: 'transparent', padding: 0 }}
        />
      </div>
    </CollapsibleCard>
  );
}

function BrainDumpRowPhone({
  note,
  objectives,
  priorityCapReached,
  onChanged,
}: {
  readonly note: NoteView;
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly priorityCapReached: boolean;
  readonly onChanged: () => void;
}) {
  const api = useDomainApi();
  const [promoteOpen, setPromoteOpen] = useState(false);

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) 0' }}>
      <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>{note.text}</span>
      <div style={{ display: 'flex', gap: 'var(--space-1)', flex: 'none' }}>
        <button
          type="button"
          aria-label={`Promote ${note.text}`}
          onClick={() => setPromoteOpen(true)}
          style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}
        >
          <ArrowUpRight size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={`Delete ${note.text}`}
          onClick={() => void api.deleteNote(note.noteId).then(onChanged)}
          style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}
        >
          <Trash2 size={16} aria-hidden="true" />
        </button>
      </div>
      {promoteOpen && (
        <PromotePopoverPhone
          note={note}
          objectives={objectives}
          priorityCapReached={priorityCapReached}
          onClose={() => setPromoteOpen(false)}
          onDone={() => {
            setPromoteOpen(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/** Two-target promote popover, identical interaction to desktop's own. */
function PromotePopoverPhone({
  note,
  objectives,
  priorityCapReached,
  onClose,
  onDone,
}: {
  readonly note: NoteView;
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly priorityCapReached: boolean;
  readonly onClose: () => void;
  readonly onDone: () => void;
}) {
  const api = useDomainApi();
  const [choosingObjective, setChoosingObjective] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    }
    const id = window.setTimeout(() => document.addEventListener('mousedown', handlePointerDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Promote: ${note.text}`}
      style={{
        position: 'absolute',
        zIndex: 41,
        top: '100%',
        right: 0,
        left: 0,
        background: 'var(--surface-overlay)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-2)',
        border: '1px solid var(--border-subtle)',
        padding: 'var(--space-2)',
      }}
    >
      {!choosingObjective ? (
        <>
          <PickerOptionRow label="Turn into an action" selected={false} onSelect={() => setChoosingObjective(true)} />
          <div>
            <PickerOptionRow
              label="Turn into a Priority"
              selected={false}
              onSelect={() => {
                if (priorityCapReached) return;
                void api.promoteNote(note.noteId, { kind: 'priority' }).then(onDone);
              }}
            />
            {priorityCapReached && (
              <p style={{ margin: '0 var(--space-2) var(--space-1)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
                Five&apos;s the limit for today — finish or clear one first to add another.
              </p>
            )}
          </div>
        </>
      ) : (
        objectives.map((o) => (
          <PickerOptionRow
            key={o.objectiveId}
            label={o.title}
            selected={false}
            onSelect={() => void api.promoteNote(note.noteId, { kind: 'action', objectiveId: o.objectiveId }).then(onDone)}
          />
        ))
      )}
    </div>
  );
}
